import {
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react'
import { ChevronsLeftRight } from 'lucide-react'

/**
 * Props for the before/after image comparison slider. The "before" image is
 * drawn left of a draggable divider and the "after" image right of it, so a
 * person can inspect exactly what changed.
 */
export type CompareSliderProps = {
  /** URL of the image shown on the left of the divider (the original). */
  beforeSrc: string
  /** URL of the image shown on the right of the divider (the result). */
  afterSrc: string
  /** Short caption pinned to the left edge, e.g. "Original". */
  beforeLabel: string
  /** Short caption pinned to the right edge, e.g. "Background removed". */
  afterLabel: string
  /** Where the divider starts, as a percentage from the left (0-100). */
  defaultPosition?: number
  /** Alt text for the before image; falls back to `beforeLabel`. */
  beforeAlt?: string
  /** Alt text for the after image; falls back to `afterLabel`. */
  afterAlt?: string
  /**
   * Draw a checkerboard behind both images. Needed when the "after" image
   * has transparent pixels (a background-removal cutout): without it the
   * transparent area takes the page background and the removal is invisible.
   */
  showTransparencyGrid?: boolean
  /** Accessible name for the divider handle. */
  handleLabel?: string
  className?: string
  style?: CSSProperties
  'data-testid'?: string
  /** Fires with the new 0-100 position whenever the divider moves. */
  onPositionChange?: (position: number) => void
}

const clampPosition = (value: number) => Math.min(100, Math.max(0, value))

// This component is styled with inline styles, not the Tailwind classes the
// package's other components use, because mdl-frontend renders it and has no
// Tailwind build: Tailwind classes would paint nothing there.

/** Arrow keys move the divider by this many percent; Shift multiplies it. */
const KEYBOARD_STEP = 2
const KEYBOARD_STEP_SHIFT = 10

/**
 * Checkerboard background that makes transparent pixels visible. Shared so
 * every surface that previews an alpha cutout (the compare slider here, the
 * editor's accepted-result frame) draws the same grid.
 */
export const TRANSPARENCY_GRID_STYLE: CSSProperties = {
  backgroundColor: '#ffffff',
  backgroundImage: 'repeating-conic-gradient(#d9d9d9 0% 25%, #ffffff 0% 50%)',
  backgroundSize: '16px 16px',
}

const imageStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  pointerEvents: 'none',
  userSelect: 'none',
}

const labelStyle: CSSProperties = {
  position: 'absolute',
  top: 12,
  padding: '4px 10px',
  borderRadius: 999,
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 500,
  color: '#ffffff',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
}

/**
 * Before/after image comparison with a draggable, keyboard-operable divider.
 * Used to review an AI image alteration before deciding to accept it: the
 * MDL editor's Remove Background tool and the Case Images enhance flow both
 * put the original on the left and the generated result on the right.
 *
 * The handle is a `role="slider"` element: arrow keys nudge it, Home/End
 * jump to either edge, and pointer drag anywhere on the image moves it.
 */
export const CompareSlider = ({
  beforeSrc,
  afterSrc,
  beforeLabel,
  afterLabel,
  defaultPosition = 50,
  beforeAlt,
  afterAlt,
  showTransparencyGrid = true,
  handleLabel = 'Compare before and after',
  className,
  style,
  'data-testid': testId,
  onPositionChange,
}: CompareSliderProps) => {
  const [position, setPosition] = useState(() => clampPosition(defaultPosition))
  const containerRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const labelId = useId()

  const moveTo = (next: number) => {
    const clamped = clampPosition(Math.round(next * 10) / 10)
    setPosition(clamped)
    onPositionChange?.(clamped)
  }

  const moveToClientX = (clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) return
    moveTo(((clientX - rect.left) / rect.width) * 100)
  }

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // Only the primary button drags; a right-click should keep the divider.
    if (event.button !== 0) return
    draggingRef.current = true
    event.currentTarget.setPointerCapture?.(event.pointerId)
    moveToClientX(event.clientX)
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    moveToClientX(event.clientX)
  }

  const handlePointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? KEYBOARD_STEP_SHIFT : KEYBOARD_STEP
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        moveTo(position - step)
        break
      case 'ArrowRight':
      case 'ArrowUp':
        moveTo(position + step)
        break
      case 'Home':
        moveTo(0)
        break
      case 'End':
        moveTo(100)
        break
      default:
        return
    }
    event.preventDefault()
  }

  return (
    <div
      ref={containerRef}
      className={className}
      data-testid={testId}
      style={{
        position: 'relative',
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        cursor: 'ew-resize',
        ...(showTransparencyGrid ? TRANSPARENCY_GRID_STYLE : null),
        ...style,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      {/*
        Each image paints only its own side of the divider. The before image
        is clipped too: otherwise a transparent result (a background-removal
        cutout) shows the original through its empty pixels and looks
        unchanged, instead of showing the grid.
      */}
      <img
        src={beforeSrc}
        alt={beforeAlt ?? beforeLabel}
        draggable={false}
        style={{
          ...imageStyle,
          clipPath: `inset(0 ${Math.round((100 - position) * 10) / 10}% 0 0)`,
        }}
      />
      <img
        src={afterSrc}
        alt={afterAlt ?? afterLabel}
        draggable={false}
        data-testid={testId ? `${testId}-after` : undefined}
        style={{
          ...imageStyle,
          position: 'absolute',
          inset: 0,
          clipPath: `inset(0 0 0 ${position}%)`,
        }}
      />
      <span style={{ ...labelStyle, left: 12 }} aria-hidden="true">
        {beforeLabel}
      </span>
      <span style={{ ...labelStyle, right: 12 }} aria-hidden="true">
        {afterLabel}
      </span>
      <div
        role="slider"
        tabIndex={0}
        aria-label={handleLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(position)}
        aria-valuetext={`${Math.round(position)}% ${beforeLabel}, ${100 - Math.round(position)}% ${afterLabel}`}
        aria-describedby={labelId}
        data-testid={testId ? `${testId}-handle` : undefined}
        onKeyDown={handleKeyDown}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: `${position}%`,
          width: 2,
          marginLeft: -1,
          backgroundColor: '#ffffff',
          boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.25)',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 32,
            height: 32,
            marginTop: -16,
            marginLeft: -16,
            borderRadius: '50%',
            backgroundColor: '#ffffff',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#333333',
          }}
        >
          <ChevronsLeftRight size={16} aria-hidden="true" />
        </span>
      </div>
      <span id={labelId} hidden>
        {beforeLabel} | {afterLabel}
      </span>
    </div>
  )
}
